import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Alan, Buton, GirisKabugu, NotKutusu, OnayKutusu, SifreGucu } from "@koclukpanel/ui";
import {
  AKIS_METNI,
  AKTIVASYON_RIZA_KODLARI,
  aktivasyonOnaylari,
  aktivasyonOnaylariTamMi,
  onayAnahtari,
  GUC_ADI,
  OGRENCI_EN_AZ,
  jetonGecerliGorunuyorMu,
  jetonHatasiniSinifla,
  jetonSayfasiCozumle,
  sifreDogrula,
  sifreGucu,
  type JetonSayfasi as Sayfa,
} from "@koclukpanel/contracts";
import { ApiHatasi, davetOnayMetinleri, davetiTamamla, sifreSifirla } from "./api";

/** G-AUT-08b — React çiziminden önce okunur; tekrar çizimde jeton URL'den yeniden aranmaz. */
export function jetonSayfasiniOku(): Sayfa | null {
  const sayfa = jetonSayfasiCozumle(window.location.pathname, window.location.search);
  if (sayfa && sayfa.akis !== "anket") {
    const adres = new URL(window.location.href);
    adres.searchParams.delete("jeton");
    window.history.replaceState(
      window.history.state,
      "",
      `${adres.pathname}${adres.search}${adres.hash}`,
    );
  }
  return sayfa;
}

/** G-AUT-08b — öğrenci/veli eşiği; kabul kararı ve jeton denetimi mevcut sunucu uçlarında. */
export function JetonSayfasi({ sayfa }: { sayfa: Sayfa }) {
  const [form, setForm] = useState({ sifre: "", tekrar: "" });
  const [denendi, setDenendi] = useState(false);
  const metin = AKIS_METNI[sayfa.akis];
  const hatalar = sifreDogrula(form);
  const guc = sifreGucu(form.sifre, OGRENCI_EN_AZ);
  /*
    AKTIVASYONDA ZORUNLU ONAY — F-LGL-03a (Emre, 24.10.2026): hangi metinlerin istendiğine SUNUCU karar verir.
    18 yaş altı öğrencide liste boş döner (zorunlu rızayı veli verir) ve hiç kutu çizilmez. Kutular işaretlenmeden
    gönder pasif; asıl kapı sunucuda. Onay, kullanıcının GÖRDÜĞÜ metin sürümüyle gider.
  */
  const davetMi = sayfa.akis === "davet";
  const metinSorgu = useQuery({
    queryKey: ["davet-onay-metinleri", sayfa.jeton],
    queryFn: () => davetOnayMetinleri(sayfa.jeton),
    enabled: davetMi && jetonGecerliGorunuyorMu(sayfa.jeton),
    retry: false,
  });
  const metinler = metinSorgu.data?.metinler ?? [];
  const [isaretli, setIsaretli] = useState<Record<string, boolean>>({});
  const onayHazir =
    !davetMi || (metinSorgu.isSuccess && aktivasyonOnaylariTamMi(metinler, isaretli));
  const gonder = useMutation({
    mutationFn: async () => {
      if (sayfa.akis === "davet")
        return (
          await davetiTamamla(sayfa.jeton, form.sifre, aktivasyonOnaylari(metinler, isaretli))
        ).durum;
      await sifreSifirla(sayfa.jeton, form.sifre);
      return "AKTIF" as const;
    },
    onError: (e) => {
      // Aradan yeni metin sürümü yayınlandıysa ya da onay eksik kaldıysa: kutular sıfırlanır, güncel metin
      // yeniden okutulur (sürüm otomatik değiştirilip tekrar gönderilmez).
      if (e instanceof ApiHatasi && AKTIVASYON_RIZA_KODLARI.includes(e.kod)) {
        setIsaretli({});
        void metinSorgu.refetch();
      }
    },
  });
  const gonderHatasi = gonder.error instanceof ApiHatasi ? gonder.error : null;
  // Onay metinleri okuması da jeton reddedebilir (süresi dolmuş / kullanılmış): aynı ölü bağlantı ekranı.
  const sorguHatasi = metinSorgu.error instanceof ApiHatasi ? metinSorgu.error : null;
  const sorguJetonHatasi =
    sorguHatasi !== null &&
    jetonHatasiniSinifla({ durum: sorguHatasi.durum, kod: sorguHatasi.kod }) === "jeton";
  const hata = sorguJetonHatasi ? sorguHatasi : gonderHatasi;
  const sinif = hata
    ? jetonHatasiniSinifla({ durum: hata.durum, kod: hata.kod, ayrintiSifre: hata.ayrinti?.sifre })
    : gonder.isError
      ? "genel"
      : null;
  const giriseGit = (
    <Buton
      boyut="genis"
      onClick={() => {
        window.location.href = "/";
      }}
    >
      Giriş ekranına git
    </Buton>
  );

  if (!jetonGecerliGorunuyorMu(sayfa.jeton) || sinif === "jeton")
    return (
      <GirisKabugu
        baslik="Bağlantı geçersiz"
        aciklama={hata?.message ?? "E-postadaki bağlantı eksik ya da bozuk görünüyor."}
      >
        <NotKutusu ton="uyari">
          {sayfa.akis === "davet"
            ? "Yeni bir davet bağlantısı için kurumla iletişime geçin."
            : "Giriş ekranındaki Şifremi unuttum bağlantısından yeni bir bağlantı isteyin."}
        </NotKutusu>
        {giriseGit}
      </GirisKabugu>
    );
  if (gonder.isSuccess)
    return (
      <GirisKabugu baslik="Şifreniz kaydedildi" aciklama={metin.bitti}>
        {giriseGit}
      </GirisKabugu>
    );
  return (
    <GirisKabugu baslik={metin.baslik} aciklama={metin.aciklama} altNot={metin.altNot}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setDenendi(true);
          if (!Object.keys(hatalar).length && onayHazir && !gonder.isPending) gonder.mutate();
        }}
      >
        {sinif === "genel" && (
          <NotKutusu ton="hata" duyur>
            {hata?.message ?? "İşlem tamamlanamadı. Bağlantınızı kontrol edip tekrar deneyin."}
          </NotKutusu>
        )}
        <Alan
          etiket="Yeni şifre"
          type="password"
          autoComplete="new-password"
          value={form.sifre}
          onChange={(e) => setForm((f) => ({ ...f, sifre: e.target.value }))}
          hata={(denendi ? hatalar.sifre : undefined) ?? hata?.ayrinti?.sifre}
        />
        <SifreGucu seviye={guc} ad={GUC_ADI[guc]} />
        <Alan
          etiket="Yeni şifre (tekrar)"
          type="password"
          autoComplete="new-password"
          value={form.tekrar}
          onChange={(e) => setForm((f) => ({ ...f, tekrar: e.target.value }))}
          hata={denendi ? hatalar.tekrar : undefined}
        />
        <p>
          Öğrenci ve veli hesaplarında en az {OGRENCI_EN_AZ} karakter. Çok yaygın kullanılan
          şifreler kabul edilmez.
        </p>
        {davetMi && metinSorgu.isError && !sorguJetonHatasi && (
          <NotKutusu ton="hata" duyur>
            Onay metinleri alınamadı. Bağlantınızı kontrol edip{" "}
            <button type="button" className="underline" onClick={() => void metinSorgu.refetch()}>
              tekrar deneyin
            </button>
            .
          </NotKutusu>
        )}
        {metinler.length > 0 && (
          <fieldset className="flex flex-col gap-2">
            <legend>Hesabı açmak için onaylayın</legend>
            {metinler.map((m) => (
              <div key={m.tur} className="flex flex-col gap-1">
                <OnayKutusu
                  checked={isaretli[onayAnahtari(m)] === true}
                  onChange={(e) =>
                    setIsaretli((x) => ({ ...x, [onayAnahtari(m)]: e.target.checked }))
                  }
                  etiket={
                    <>
                      <strong>{m.baslik}</strong> metnini okudum, onaylıyorum.
                    </>
                  }
                />
                <details className="pl-[26px]">
                  <summary className="cursor-pointer text-xs">Metni oku (sürüm {m.surum})</summary>
                  <p className="mt-1 text-xs whitespace-pre-wrap">{m.govde}</p>
                </details>
              </div>
            ))}
          </fieldset>
        )}
        <Buton
          type="submit"
          boyut="genis"
          disabled={!onayHazir}
          yukleniyor={gonder.isPending}
          yukleniyorMetni="Gönderiliyor"
        >
          {metin.dugme}
        </Buton>
      </form>
    </GirisKabugu>
  );
}
